import "~/styles/tailwind/tailwind.css";
import { RemixBrowser } from "@remix-run/react";
import ShopifyFormat from "@shopify/i18next-shopify";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { startTransition, StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { getInitialNamespaces } from "remix-i18next/client";
import i18nextOptions from "./i18n/i18nextOptions";
import "~/lib/sentry.client";

function hydrate() {
  const mode = window.__RENDER_MODE__ || "create"; // Fallback

  if (mode === "hydrate") {
    startTransition(() => {
      hydrateRoot(
        document,
        <StrictMode>
          <I18nextProvider i18n={i18next}>
            <RemixBrowser />
          </I18nextProvider>
        </StrictMode>,
      );
    });
  } else {
    const container = document.getElementById("MainContent");

    if (!container) {
      console.error("Hydration target #MainContent not found");
      return;
    }

    startTransition(() => {
      createRoot(container).render(
        <StrictMode>
          <I18nextProvider i18n={i18next}>
            <RemixBrowser />
          </I18nextProvider>
        </StrictMode>,
      );
    });
  }
}

async function initI18n() {
  // Safety check: ensure window.ENV is available
  if (!window.ENV) {
    console.warn("window.ENV not available, waiting for it to be set...");
    // Wait a bit for window.ENV to be injected
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // Fallback if ENV is still not available
  const debugMode = window.ENV?.NODE_ENV === "development" || false;

  // Safety check: ensure DOM is ready
  if (document.readyState !== "complete") {
    console.warn("DOM not ready, waiting...");
    await new Promise((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        window.addEventListener("load", resolve);
      }
    });
  }

  await i18next
    .use(initReactI18next)
    .use(ShopifyFormat.default || ShopifyFormat)
    .use(LanguageDetector)
    .use(Backend)
    .init({
      ...i18nextOptions,
      // @ts-ignore We set ENV in root.tsx
      debug: debugMode,
      ns: (() => {
        try {
          return getInitialNamespaces() || [];
        } catch (error) {
          console.warn("Error getting initial namespaces:", error);
          return [];
        }
      })(),
      backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
      detection: {
        order: ["htmlTag"],
        caches: [],
      },
    });
}

initI18n().then(hydrate);
